import { useContext } from 'react';

import { ProgramOrGuideItem } from '@/components/app/cards/PacksList/types';
import { useMediaPlayerContext } from '@/components/app/contentPlayer/MediaPlayerProvider';
import { AuthModalContext } from '@/context/AuthModalContextProvider';
import { useAnalytics } from '@/hooks/analytics';
import { useGuidePlay } from '@/hooks/app/useGuidePlay';
import { useLockedContentRouting } from '@/hooks/app/useLockedContentRouting';
import { useGuideState, useUserState } from '@/hooks/store';
import { useRouterPush } from '@/hooks/utils/useRouterPush';
import { Guide } from '@/store/guide/types';
import { Program } from '@/store/program/types';

interface GuideActivationState {
	isLocked: boolean;
	isDisabled: boolean;
}

// Shared by every control that starts a guide from a program page, so a row tap and the Play
// button gate, upsell and play in exactly the same order.
export const useGuideActivation = (
	guide: Guide | undefined,
	program: Program,
	state: GuideActivationState,
) => {
	const { logEvent } = useAnalytics();
	const onGuidePlay = useGuidePlay();
	const user = useUserState();
	const currentGuide = useGuideState();
	const routerPush = useRouterPush();
	const routeToUpsell = useLockedContentRouting();
	const { setIsModalOpen } = useContext(AuthModalContext);
	const { playerMode, onPlay, onPause } = useMediaPlayerContext();

	const isLive = Boolean(guide) && currentGuide?.id === guide?.id && playerMode !== 'stopped';

	const activate = async () => {
		if (!guide) {
			return;
		}

		if (!user) {
			setIsModalOpen(true);
			return;
		}

		if (state.isLocked) {
			logEvent({
				eventName: 'Web : Locked Content : Clicked',
				eventProps: {
					title: guide.title,
					id: program.id,
					guide_id: guide.id,
					query: null,
					filter_category: null,
					filter_duration: null,
					location: null,
					pack_item_index: null,
					query_id: null,
					search_index: null,
					search_object_id: `guide_${guide.id}`,
				},
			});

			const packItem: ProgramOrGuideItem = {
				type: 'guide',
				guide_id: guide.id,
				program_id: program.id,
				title: guide.title,
				subtitle: guide.subtitle ?? '',
				narrator_name: guide.narrator_name,
				description: program.description ?? '',
				content_type: program.content_type,
				promotional_text: '',
				is_new: false,
				is_unlocked: false,
				duration: guide.asset?.duration ?? 0,
				image: guide.cell_image ?? program.background_image,
			};
			await routeToUpsell(packItem);
			return;
		}

		if (state.isDisabled) {
			return;
		}

		if (isLive) {
			if (playerMode === 'playing') {
				onPause();
			} else {
				onPlay();
			}
			return;
		}

		if (program?.meditation_type === 'timer') {
			await routerPush(`/app/player/${guide.id}`);
		} else {
			await onGuidePlay(guide.id, program);
		}
	};

	return { activate, isLive, playerMode };
};
